/**
 * The current host's external ("off-platform") bookings.
 *
 * A **shared** duck rather than a slice per page: Próximas, Pasadas and Ingresos
 * all need the same list, and the reservation pages already suffer from having the
 * same transition sets copy-pasted across three ducks (see docs/tech-debt.md) —
 * no reason to add a fourth triplication.
 *
 * These bookings are not Sharetribe entities, so they never go into
 * `marketplaceData`: they arrive from `/api/external-bookings` as plain JSON with
 * money in subunits.
 *
 * Fully fail-safe. When `DATABASE_URL` is unset server-side the endpoint answers
 * `{ enabled: false, bookings: [] }`, and any error resolves to "feature off" —
 * the host reservation pages must keep working without this feature.
 *
 * See docs/external-bookings.md.
 */

import { cancelExternalBooking, createExternalBooking, getExternalBookings } from '../util/api';
import { storableError } from '../util/errors';
import { isExternalBookingActive } from '../util/externalBookings';
import { denormalisedResponseEntities } from '../util/data';
import { monthIdString } from '../util/dates';
import { types as sdkTypes } from '../util/sdkLoader';

const { UUID } = sdkTypes;

// ================ Action types ================ //

export const FETCH_EXTERNAL_BOOKINGS_REQUEST = 'app/externalBookings/FETCH_REQUEST';
export const FETCH_EXTERNAL_BOOKINGS_SUCCESS = 'app/externalBookings/FETCH_SUCCESS';
export const FETCH_EXTERNAL_BOOKINGS_ERROR = 'app/externalBookings/FETCH_ERROR';

export const FETCH_HOST_LISTINGS_SUCCESS = 'app/externalBookings/FETCH_LISTINGS_SUCCESS';

export const FETCH_LISTING_TIME_SLOTS_REQUEST = 'app/externalBookings/FETCH_TIME_SLOTS_REQUEST';
export const FETCH_LISTING_TIME_SLOTS_SUCCESS = 'app/externalBookings/FETCH_TIME_SLOTS_SUCCESS';
export const FETCH_LISTING_TIME_SLOTS_ERROR = 'app/externalBookings/FETCH_TIME_SLOTS_ERROR';

export const CREATE_EXTERNAL_BOOKING_REQUEST = 'app/externalBookings/CREATE_REQUEST';
export const CREATE_EXTERNAL_BOOKING_SUCCESS = 'app/externalBookings/CREATE_SUCCESS';
export const CREATE_EXTERNAL_BOOKING_ERROR = 'app/externalBookings/CREATE_ERROR';

// ================ Reducer ================ //

const initialState = {
  // null = not asked yet; false = no database / not available.
  enabled: null,
  bookings: [],
  fetchInProgress: false,
  fetchError: null,
  // The host's published listings — the modal needs a picker when there is more
  // than one, since a booking has to be assigned to a pool.
  hostListings: [],
  // The calendar in "Proteger reserva externa" disables days a pool has no
  // availability left for, same idea as the booking-time picker. Keyed by
  // listingId, then by month ('YYYY-MM'), mirroring ListingPage's monthlyTimeSlots.
  listingTimeSlots: {},
  createInProgress: false,
  createError: null,
};

export default function externalBookingsReducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case FETCH_EXTERNAL_BOOKINGS_REQUEST:
      return { ...state, fetchInProgress: true, fetchError: null };
    case FETCH_EXTERNAL_BOOKINGS_SUCCESS:
      return {
        ...state,
        fetchInProgress: false,
        enabled: payload.enabled,
        bookings: payload.bookings,
      };
    case FETCH_EXTERNAL_BOOKINGS_ERROR:
      // Treat an error as "feature unavailable" so the UI hides instead of
      // showing a broken panel on a page that has plenty else to render.
      return { ...state, fetchInProgress: false, enabled: false, fetchError: payload };

    case FETCH_HOST_LISTINGS_SUCCESS:
      return { ...state, hostListings: payload };

    case FETCH_LISTING_TIME_SLOTS_REQUEST: {
      const { listingId, monthId } = payload;
      const forListing = state.listingTimeSlots[listingId] || {};
      return {
        ...state,
        listingTimeSlots: {
          ...state.listingTimeSlots,
          [listingId]: {
            ...forListing,
            [monthId]: { ...forListing[monthId], fetchTimeSlotsInProgress: true },
          },
        },
      };
    }
    case FETCH_LISTING_TIME_SLOTS_SUCCESS: {
      const { listingId, monthId, timeSlots } = payload;
      const forListing = state.listingTimeSlots[listingId] || {};
      return {
        ...state,
        listingTimeSlots: {
          ...state.listingTimeSlots,
          [listingId]: {
            ...forListing,
            [monthId]: { fetchTimeSlotsInProgress: false, timeSlots },
          },
        },
      };
    }
    case FETCH_LISTING_TIME_SLOTS_ERROR: {
      const { listingId, monthId, error } = payload;
      const forListing = state.listingTimeSlots[listingId] || {};
      return {
        ...state,
        listingTimeSlots: {
          ...state.listingTimeSlots,
          [listingId]: {
            ...forListing,
            [monthId]: { fetchTimeSlotsInProgress: false, fetchTimeSlotsError: error },
          },
        },
      };
    }

    case CREATE_EXTERNAL_BOOKING_REQUEST:
      return { ...state, createInProgress: true, createError: null };
    case CREATE_EXTERNAL_BOOKING_SUCCESS:
      return {
        ...state,
        createInProgress: false,
        bookings: [payload.booking, ...state.bookings],
      };
    case CREATE_EXTERNAL_BOOKING_ERROR:
      return { ...state, createInProgress: false, createError: payload };

    default:
      return state;
  }
}

// ================ Action creators ================ //

const fetchExternalBookingsRequest = () => ({ type: FETCH_EXTERNAL_BOOKINGS_REQUEST });
const fetchExternalBookingsSuccess = payload => ({
  type: FETCH_EXTERNAL_BOOKINGS_SUCCESS,
  payload,
});
const fetchExternalBookingsError = e => ({
  type: FETCH_EXTERNAL_BOOKINGS_ERROR,
  payload: e,
  error: true,
});

// ================ Selectors ================ //

/** Whether to show the external-bookings UI at all. */
export const externalBookingsEnabled = state => state.externalBookings.enabled === true;

/** Bookings that hold a calendar slot — the ones worth listing. */
export const activeExternalBookings = state =>
  state.externalBookings.bookings.filter(isExternalBookingActive);

/**
 * Pending offers whose slot has not passed — they belong with the UNCONFIRMED
 * reservations ("En espera"), not with the confirmed ones: the bañista has not booked
 * yet. Listing them under Próximas made a reservation travel backwards (Próximas →
 * En espera → Próximas) as it progressed.
 *
 * A converted offer is NOT here: it became a real transaction and the lists already
 * show it like any other.
 */
export const pendingExternalBookings = (state, now = new Date()) =>
  activeExternalBookings(state)
    .filter(b => new Date(b.bookingEnd) > now)
    .sort((a, b) => new Date(a.bookingStart) - new Date(b.bookingStart));

// ================ Thunks ================ //

export const fetchExternalBookings = () => dispatch => {
  dispatch(fetchExternalBookingsRequest());
  return getExternalBookings()
    .then(response => {
      dispatch(
        fetchExternalBookingsSuccess({
          enabled: response.enabled === true,
          bookings: Array.isArray(response.bookings) ? response.bookings : [],
        })
      );
      return response;
    })
    .catch(e => {
      // Swallowed on purpose: a page's loadData must not fail over this. But the
      // reducer turns any failure into "feature off", which looks exactly like the
      // intended no-database degradation — so leave a trace, or a broken endpoint is
      // an invisible missing button.
      console.error('External bookings unavailable:', e?.message || e); // eslint-disable-line
      dispatch(fetchExternalBookingsError(storableError(e)));
    });
};

/**
 * The host's published listings, for the modal's pool picker. Best-effort: without
 * it the modal falls back to the single listing it knows about.
 */
export const fetchHostListingsForExternalBooking = () => (dispatch, getState, sdk) =>
  sdk.ownListings
    .query({
      states: ['published'],
      perPage: 100,
      // `priceRange` carries the host's own guest tiers, which is where the modal's
      // "Número de personas" options come from — the same format as the rest of the
      // platform, not an invented list. `availabilityPlan` gives the timezone the
      // date picker needs to query time slots in the pool's own day boundaries.
      // `searchListingType` is the category (the field the search filters on, as
      // `pub_searchListingType`), so the modal can preselect the pool.
      'fields.listing': [
        'title',
        'state',
        'publicData.priceRange',
        'publicData.searchListingType',
        'availabilityPlan',
      ],
    })
    .then(response => {
      const listings = (response.data.data || []).map(l => ({
        id: l.id.uuid,
        title: l.attributes.title,
        guestTiers: (l.attributes.publicData?.priceRange || [])
          .map(row => row && row.quantityRange)
          .filter(Boolean),
        timeZone: l.attributes.availabilityPlan?.timezone || null,
        searchListingType: l.attributes.publicData?.searchListingType || null,
      }));
      dispatch({ type: FETCH_HOST_LISTINGS_SUCCESS, payload: listings });
      return listings;
    })
    .catch(() => {
      dispatch({ type: FETCH_HOST_LISTINGS_SUCCESS, payload: [] });
    });

/**
 * Time slots for a pool's calendar, one month at a time — same query Sharetribe's
 * own booking pickers use, so "blocked" here already accounts for every existing
 * reservation (and this offer's own pending calendar hold) without us having to
 * separately look up bookings.
 */
export const fetchListingTimeSlots = (listingId, start, end, timeZone) => (
  dispatch,
  getState,
  sdk
) => {
  const monthId = monthIdString(start, timeZone);
  dispatch({ type: FETCH_LISTING_TIME_SLOTS_REQUEST, payload: { listingId, monthId } });
  return sdk.timeslots
    .query({ listingId: new UUID(listingId), start, end, perPage: 500, page: 1 })
    .then(response => {
      const timeSlots = denormalisedResponseEntities(response);
      dispatch({
        type: FETCH_LISTING_TIME_SLOTS_SUCCESS,
        payload: { listingId, monthId, timeSlots },
      });
    })
    .catch(e => {
      // Logged rather than swallowed: the calendar fails OPEN on this (a broken
      // fetch must never read as "the whole month is booked"), so a real problem
      // here would otherwise be invisible.
      console.error('Could not load time slots for external booking calendar:', e?.message || e); // eslint-disable-line
      dispatch({
        type: FETCH_LISTING_TIME_SLOTS_ERROR,
        payload: { listingId, monthId, error: storableError(e) },
      });
    });
};

/**
 * Create an external booking. The server recomputes the money from
 * `agreedPriceSubunits` alone, so this only has to send what the host typed.
 *
 * Resolves with the created booking on success and `null` on failure (the error
 * lands in state for the modal to show).
 */
export const createExternalBookingThunk = values => dispatch => {
  dispatch({ type: CREATE_EXTERNAL_BOOKING_REQUEST });
  return createExternalBooking(values)
    .then(response => {
      dispatch({ type: CREATE_EXTERNAL_BOOKING_SUCCESS, payload: response });
      return response;
    })
    .catch(e => {
      dispatch({ type: CREATE_EXTERNAL_BOOKING_ERROR, payload: storableError(e) });
      return null;
    });
};

/**
 * Cancel a booking and refresh the list, so the panel reflects the new status (and
 * the freed calendar slot) without a page reload.
 */
export const cancelExternalBookingThunk = ({ id, reason }) => dispatch =>
  cancelExternalBooking({ id, reason })
    .then(() => dispatch(fetchExternalBookings()))
    .catch(e => {
      dispatch(fetchExternalBookingsError(storableError(e)));
      return null;
    });
